/**
 * Staff and their roles.
 *
 * Nothing in the product works until somebody has been invited, so this is
 * the page a new customer sees first after their own account exists.
 *
 * Roles are checkboxes rather than a single select, because a member holds an
 * array of them — author and approver at once is normal in a hospital small
 * enough that the person who writes a policy also signs it off. A dropdown
 * would quietly make that impossible to express.
 */

import { Link } from 'react-router-dom';
import { useMemo, useState, type FormEvent } from 'react';
import { useAuth } from '../../auth/useAuth';
import {
  useCancelInvite,
  useInviteUser,
  useMembers,
  usePendingInvites,
  useRemoveUser,
  useResendInvite,
  useSetUserRoles,
  type Member,
  useUpdateMemberProfile,
  useMemberHistory,
  type RemovalResult,
} from './useMembers';
import { ASSIGNABLE_ROLES, ROLE_DISPLAY_NAMES, type Role } from '../../types/roles';
import { isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import styles from '../../shared/Page.module.css';
import own from './Admin.module.css';
import { EMAIL_MAX, FILTER_MAX, PERSON_NAME_MAX, TITLE_MAX } from '../../types/fieldLimits';
import { useTaxonomy, type TaxonomyItem } from './useTaxonomy';
import { CopyLink } from './CopyLink';
import { MultiSelect, type MultiSelectOption } from '../../shared/MultiSelect';
import { BulkInvite } from './BulkInvite';
import { ResetMfaButton } from '../console/ResetMfaButton';

/** The roles an administrator may hand out. Sysadmin is not among them. */
const ASSIGNABLE: Role[] = [...ASSIGNABLE_ROLES];

function errorText(e: unknown, fallback: string): string {
  if (isCallFailure(e)) return e.message;
  if (e instanceof Error) return e.message;
  return fallback;
}

/** What each role lets a person do, shown beside its name in the list. */
const ROLE_HINTS: Record<Role, string> = {
  viewer: 'Reads and acknowledges policies',
  author: 'Writes and submits policies',
  reviewer: 'Reviews what authors submit',
  approver: 'Approves and publishes',
  admin: 'Staff, workflows, taxonomy and settings',
  sysadmin: 'Names System Admins and sets up sign-in. Google Authenticator required.',
};
const ROLE_OPTIONS: MultiSelectOption[] = ASSIGNABLE.map((r) => ({ id: r, name: ROLE_DISPLAY_NAMES[r], hint: ROLE_HINTS[r] }));
/**
 * The System Admin role is on the list only for someone who already holds
 * it: the backend refuses it from anyone else, and a choice that can only
 * fail is worse than none. An organization keeps at least one.
 */
const ROLE_OPTIONS_WITH_SYSADMIN: MultiSelectOption[] = [...ROLE_OPTIONS, { id: 'sysadmin', name: ROLE_DISPLAY_NAMES.sysadmin, hint: ROLE_HINTS.sysadmin }];
const roleOptionsFor = (canNameSysAdmin: boolean) => (canNameSysAdmin ? ROLE_OPTIONS_WITH_SYSADMIN : ROLE_OPTIONS);
const departmentOptions = (departments: TaxonomyItem[]): MultiSelectOption[] => departments.map((d) => ({ id: d.id, name: d.name }));

/** Which departments a person is in: a select-shaped field whose list floats over the page. */
function DepartmentPicker({ id, label, value, onChange, disabled, departments }: {
  id: string; label: string; value: string[]; onChange: (next: string[]) => void; disabled?: boolean; departments: TaxonomyItem[];
}) {
  return (
    <MultiSelect
      id={id}
      label={label}
      options={departmentOptions(departments)}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder="No department"
      emptyText="No departments yet. Add them under Taxonomy."
    />
  );
}

function InviteForm({ orgId }: { orgId: string | null }) {
  const invite = useInviteUser(orgId);
  const { session } = useAuth();
  const canNameSysAdmin = session?.roles.includes('sysadmin') ?? false;
  const departments = useTaxonomy('department', Boolean(orgId));
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [title, setTitle] = useState('');
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [roles, setRoles] = useState<Role[]>(['viewer']);
  const [touched, setTouched] = useState(false);

  const emailInvalid = touched && !email.trim().includes('@');
  const noRole = touched && roles.length === 0;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!email.trim().includes('@') || roles.length === 0) return;

    invite.mutate(
      {
        email: email.trim(),
        roles,
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(departmentIds.length ? { departmentIds } : {}),
      },
      {
        onSuccess: () => {
          setEmail(''); setFirstName(''); setLastName(''); setTitle(''); setDepartmentIds([]);
          setRoles(['viewer']);
          setTouched(false);
        },
      },
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      {invite.error && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {errorText(invite.error, 'The invitation could not be sent.')}
        </div>
      )}
      {invite.isSuccess && (
        <div className={`${styles.notice} ${invite.data?.emailSent === false ? styles.noticeWarn : styles.noticeInfo}`} role="status">
          {/* The backend's own sentence: "sent", or "created but the email
              could not go" with what to do. A fixed "Invitation sent" here
              once hid every delivery failure. */}
          {invite.data?.message ?? 'Invitation sent. They will receive a link to join.'}
          {invite.data?.emailSent === false && invite.data.inviteLink && (
            <CopyLink link={invite.data.inviteLink} />
          )}
        </div>
      )}

      <div className={own.inviteGrid}>
        <div className={own.inviteField}>
          <label className={own.label} htmlFor="invite-email">
            Work email
          </label>
          <input
            id="invite-email"
            maxLength={EMAIL_MAX}
            className={own.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@hospital.org"
            aria-invalid={emailInvalid}
            aria-describedby={emailInvalid ? 'invite-email-error' : undefined}
          />
        </div>

        <div className={own.inviteField}>
          <label className={own.label} htmlFor="invite-first-name">First name</label>
          <input
            id="invite-first-name"
            maxLength={PERSON_NAME_MAX}
            className={own.input}
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="off"
          />
        </div>

        <div className={own.inviteField}>
          <label className={own.label} htmlFor="invite-last-name">Last name</label>
          <input
            id="invite-last-name"
            maxLength={PERSON_NAME_MAX}
            className={own.input}
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="off"
          />
        </div>

        <div className={own.inviteField}>
          <label className={own.label} htmlFor="invite-title">Title</label>
          <input
            id="invite-title"
            maxLength={TITLE_MAX}
            className={own.input}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoComplete="off"
          />
        </div>

        <div className={own.inviteField}>
          <span className={own.label} id="invite-departments-label" aria-hidden="true">Departments</span>
          <DepartmentPicker id="invite-departments" label="Departments" value={departmentIds} onChange={setDepartmentIds} departments={departments.data ?? []} />
        </div>

        <div className={own.inviteField}>
          <span className={own.label} id="invite-roles-label" aria-hidden="true">Roles</span>
          <MultiSelect
            id="invite-roles"
            label="Roles"
            options={roleOptionsFor(canNameSysAdmin)}
            value={roles}
            onChange={(next) => setRoles(next as Role[])}
            placeholder="Choose a role"
            searchable={false}
            invalid={noRole}
            describedBy={noRole ? 'invite-roles-error' : undefined}
          />
        </div>

        <div className={own.inviteAction}>
          <button
            type="submit"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={invite.isPending}
          >
            {invite.isPending ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      </div>
      {(emailInvalid || noRole) && (
        <div className={own.inviteErrors}>
          {emailInvalid && <p className={own.fieldError} id="invite-email-error">Enter the email address to invite.</p>}
          {noRole && <p className={own.fieldError} id="invite-roles-error">Choose at least one role.</p>}
        </div>
      )}
    </form>
  );
}

/** An administrator sets which departments a member is in; the change saves on each toggle. */
function DepartmentEditor({ member, orgId, departments }: { member: Member; orgId: string | null; departments: TaxonomyItem[] }) {
  const update = useUpdateMemberProfile(orgId);
  return (
    <DepartmentPicker
      id={`departments-${member.uid}`}
      label={`Departments for ${member.displayName}`}
      value={member.departmentIds}
      disabled={update.isPending}
      departments={departments}
      onChange={(next) => update.mutate({ targetUid: member.uid, departmentIds: next })}
    />
  );
}

function RoleEditor({ member, orgId }: { member: Member; orgId: string | null }) {
  const setRoles = useSetUserRoles(orgId);
  const { session } = useAuth();
  const isSelf = session?.uid === member.uid;
  const canNameSysAdmin = session?.roles.includes('sysadmin') ?? false;

  // Only another System Admin (or StandingDocs, from the console) changes a
  // System Admin; the backend refuses everyone else. Say so instead of
  // offering checkboxes whose every click would fail.
  if (member.roles.includes('sysadmin') && !canNameSysAdmin) {
    return (
      <p className={own.hint} style={{ margin: 0 }}>
        <strong>System Admin</strong>, changed only by another System Admin or by StandingDocs.
      </p>
    );
  }

  return (
    <div>
      <MultiSelect
        id={`roles-${member.uid}`}
        label={`Roles for ${member.displayName}`}
        options={roleOptionsFor(canNameSysAdmin)}
        value={member.roles}
        disabled={setRoles.isPending || isSelf}
        searchable={false}
        placeholder="No role"
        onChange={(next) => {
          // The backend requires at least one; refusing here avoids a round
          // trip that can only fail.
          if (next.length === 0) return;
          setRoles.mutate({ targetUid: member.uid, roles: next as Role[] });
        }}
      />
      {isSelf && (
        // Not a security control — the backend decides — but removing your own
        // administrator role locks you out of the page that could restore it.
        <p className={own.hint}>You cannot change your own roles.</p>
      )}
      {setRoles.error && (
        <p className={own.hint} role="alert" style={{ color: 'var(--danger-ink)' }}>
          {isCallFailure(setRoles.error) ? setRoles.error.message : 'The roles could not be changed.'}
        </p>
      )}
    </div>
  );
}

export function UsersPage() {
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;

  const members = useMembers(orgId);

  const departments = useTaxonomy('department', Boolean(orgId));
  const invites = usePendingInvites(orgId);
  const removeUser = useRemoveUser(orgId);
  const cancelInvite = useCancelInvite(orgId);
  const resendInvite = useResendInvite(orgId);

  const [query, setQuery] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [resetNote, setResetNote] = useState<string | null>(null);
  /** What the last removal left behind, so it is said once and plainly. */
  const [removal, setRemoval] = useState<RemovalResult | null>(null);
  /** Whose membership story is open. */
  const [historyFor, setHistoryFor] = useState<Member | null>(null);

  const rows = useMemo(() => {
    const all = members.data ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (m) =>
        m.displayName.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        m.roles.some((r) => ROLE_DISPLAY_NAMES[r].toLowerCase().includes(q)),
    );
  }, [members.data, query]);

  if (members.isLoading) return <Spinner label="Loading staff" />;
  // A failed read is not an empty organization: without this the page said
  // "Nobody has joined yet" to an administrator with a full staff list.
  if (members.error) {
    return (
      <PageState title="Could not load your staff" tone="alert">
        {errorText(members.error, 'The list did not come back. Reload the page to try again.')}
      </PageState>
    );
  }

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>Staff</h1>
        <p className={styles.subtitle}>
          Everyone in the organization and what each of them can do. Somebody
          can hold more than one role.
        </p>
      </header>

      <div className={styles.body}>
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            Staff
            <span className={own.countSuffix}> · {members.data?.length ?? 0}</span>
          </h2>

          {/* One person at a time, on a single line above the list. */}
          <InviteForm orgId={orgId} />

          <div className={own.uploadRow}>
            <button
              type="button"
              className={styles.btn}
              aria-expanded={showUpload}
              aria-controls="staff-upload"
              onClick={() => setShowUpload((v) => !v)}
            >
              {showUpload ? 'Hide the CSV upload' : 'Upload a CSV of several people'}
            </button>
          </div>
          {showUpload && (
            <div id="staff-upload" className={own.uploadPanel}>
              <BulkInvite orgId={orgId} departments={departments.data ?? []} />
            </div>
          )}

          {resetNote && <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">{resetNote}</div>}

          {/*
            WHAT A REMOVAL LEFT BEHIND.

            Ownership is never moved automatically: a governance duty belongs
            to the person who accepted it, and handing one over quietly would
            put a review on somebody who never agreed. So the administrator is
            told instead, once, with the policies named and a way to each one.
            They are findable again afterwards under "Owner inactive" on the
            policy list.
          */}
          {historyFor && (
            <MemberHistoryPanel orgId={orgId} member={historyFor} onClose={() => setHistoryFor(null)} />
          )}

          {removal && (
            <div
              className={`${styles.notice} ${removal.leftBehind.length > 0 ? styles.noticeWarn : styles.noticeInfo}`}
              role="status"
            >
              <p style={{ margin: 0 }}>{removal.message}</p>
              {removal.requirementsClosed !== null && removal.requirementsClosed > 0 && (
                <p className={styles.itemMeta} style={{ margin: '4px 0 0' }}>
                  {removal.requirementsClosed} {removal.requirementsClosed === 1 ? 'policy they had not yet read is' : 'policies they had not yet read are'} no longer counted as outstanding.
                  Anything they did acknowledge is untouched.
                </p>
              )}
              {removal.leftBehind.length > 0 && (
                <ul className={styles.list} style={{ marginTop: 'var(--sp-3)' }}>
                  {removal.leftBehind.map((p) => (
                    <li key={p.policyId} className={styles.listItem}>
                      <div>
                        <div className={styles.itemName}>
                          <Link to={`/policy/${p.policyId}`}>{p.policyNumber ? `${p.policyNumber} · ` : ''}{p.title}</Link>
                        </div>
                        <div className={styles.itemMeta}>{p.as === 'owner' ? 'Owner' : 'Co-owner'} · {p.status}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {removal.leftBehind.length > 0 && (
                <p className={styles.itemMeta} style={{ margin: '4px 0 0' }}>
                  Open each one and change its owner. You can find them again later under{' '}
                  <Link to="/policies?view=ownerInactive">Owner inactive</Link> on the policy list.
                </p>
              )}
              <button type="button" className={styles.btn} style={{ marginTop: 'var(--sp-3)' }} onClick={() => setRemoval(null)}>
                Dismiss
              </button>
            </div>
          )}
          {(removeUser.error || cancelInvite.error || resendInvite.error) && (
            <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
              {errorText(
                removeUser.error ?? cancelInvite.error ?? resendInvite.error,
                'That change could not be made.',
              )}
            </div>
          )}

          <div className={own.bar}>
            <div className={own.search}>
              <label className="vh" htmlFor="staff-filter">
                Filter staff by name, email or role
              </label>
              <input
                id="staff-filter"
            maxLength={FILTER_MAX}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter by name, email or role"
              />
            </div>
            <div className={own.count} role="status" aria-live="polite">
              <b>{rows.length}</b> {rows.length === 1 ? 'person' : 'people'}
            </div>
          </div>

          <div className={own.scroller}>
            <table className={own.table}>
              <caption>Staff in this organization and the roles they hold</caption>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Departments</th>
                  <th scope="col">Roles</th>
                  <th scope="col">
                    <span className="vh">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.uid}>
                    <td style={{ fontWeight: 500 }}>
                      {m.displayName}
                      {m.title && <div className={styles.itemMeta}>{m.title}</div>}
                    </td>
                    <td style={{ color: 'var(--ink-2)' }}>{m.email}</td>
                    <td><DepartmentEditor member={m} orgId={orgId} departments={departments.data ?? []} /></td>
                    <td><RoleEditor member={m} orgId={orgId} /></td>
                    <td>
                      {/* The lost phone: another System Admin resets it. The backend refuses anyone else. */}
                      {session?.uid !== m.uid && m.roles.includes('sysadmin') && session?.roles.includes('sysadmin') && orgId && (
                        <ResetMfaButton targetUid={m.uid} orgId={orgId} who={m.displayName} onDone={setResetNote} />
                      )}
                      {session?.uid !== m.uid && !m.roles.includes('sysadmin') && (
                        <button
                          type="button"
                          className={styles.btn}
                          onClick={() => removeUser.mutate({ userId: m.uid }, { onSuccess: setRemoval })}
                          disabled={removeUser.isPending}
                        >
                          Remove
                        </button>
                      )}
                      <button
                        type="button"
                        className={styles.btn}
                        onClick={() => setHistoryFor(m)}
                        aria-label={`History for ${m.displayName}`}
                      >
                        History
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.length === 0 && (
            (members.data?.length ?? 0) === 0 ? (
              <PageState title="Nobody has joined yet">
                Invite your staff with the form above. Each person gets an email, signs in
                with a link, and appears here.
              </PageState>
            ) : (
              <PageState title={`Nobody matches "${query.trim()}"`}>
                Search covers names, email addresses and roles. Clear it to see everyone.
              </PageState>
            )
          )}
        </section>

        {(invites.data?.length ?? 0) > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              Invited, not yet joined
              <span className={own.countSuffix}> · {invites.data!.length}</span>
            </h2>
            {resendInvite.isSuccess && (
              <div className={`${styles.notice} ${resendInvite.data?.emailSent === false ? styles.noticeWarn : styles.noticeInfo}`} role="status">
                {resendInvite.data?.message ?? 'Invitation resent.'}
                {resendInvite.data?.emailSent === false && resendInvite.data.inviteLink && (
                  <CopyLink link={resendInvite.data.inviteLink} />
                )}
              </div>
            )}
            <ul className={styles.list}>
              {invites.data!.map((i) => (
                <li key={i.id} className={styles.listItem}>
                  <div>
                    <div className={styles.itemName}>{i.email}</div>
                    <div className={styles.itemMeta}>
                      {i.roles.map((r) => ROLE_DISPLAY_NAMES[r]).join(', ')}
                      {i.expiresAt && ` · expires ${i.expiresAt}`}
                    </div>
                  </div>
                  <div className={styles.actions}>
                    <button
                      type="button"
                      className={styles.btn}
                      onClick={() => resendInvite.mutate({ inviteId: i.id })}
                      disabled={resendInvite.isPending}
                    >
                      Resend
                    </button>
                    <button
                      type="button"
                      className={styles.btn}
                      onClick={() => cancelInvite.mutate({ inviteId: i.id })}
                      disabled={cancelInvite.isPending}
                    >
                      Cancel
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  );
}


/**
 * ONE PERSON'S MEMBERSHIP, IN ORDER.
 *
 * "When was this person added? Who added them? What have they held since?"
 * Every answer was already recorded, in the audit log, with the actor's own
 * name as it was at the time. What was missing was somewhere to read it about
 * ONE person rather than scrolling an organization-wide log.
 *
 * So this is a read and nothing more. No second history store, no general
 * audit console, and no raw document on the screen: the server turns each
 * entry into a sentence and this shows them oldest first, because a membership
 * is a story rather than a feed.
 */
export function MemberHistoryPanel({ orgId, member, onClose }: { orgId: string | null; member: Member; onClose: () => void }) {
  const history = useMemberHistory(orgId, member.uid);

  return (
    <section className={styles.section} aria-labelledby="member-history-title">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
        <h2 className={styles.sectionTitle} id="member-history-title">
          {member.displayName || member.email}
        </h2>
        <button type="button" className={styles.btn} onClick={onClose}>Close</button>
      </div>

      {history.isLoading && <Spinner label="Loading the history" />}

      {history.isError && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {errorText(history.error, 'That history could not be loaded.')}
        </div>
      )}

      {history.data && history.data.events.length === 0 && (
        <p className={styles.itemMeta}>Nothing has been recorded about this membership yet.</p>
      )}

      {history.data && history.data.events.length > 0 && (
        <ol className={styles.list} aria-label={`History for ${member.displayName || member.email}`}>
          {history.data.events.map((e) => (
            <li key={e.id} className={styles.listItem}>
              <div>
                <div className={styles.itemName}>{e.what}</div>
                <div className={styles.itemMeta}>
                  {e.at ? new Date(e.at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                    : <span className={styles.absent}>at a time the record does not give</span>}
                  {' \u00b7 '}
                  {/* Whoever acted, as they were recorded. Never looked up
                      today: that would reassign a historical action to
                      whoever holds that account now. */}
                  {e.byName || e.byEmail || <span className={styles.absent}>somebody the record does not name</span>}
                </div>
                {e.detail && <p className={styles.itemMeta} style={{ margin: '2px 0 0' }}>{e.detail}</p>}
              </div>
            </li>
          ))}
        </ol>
      )}

      {history.data?.truncated && (
        <p className={styles.itemMeta}>
          This membership has more entries than are shown. The full record is in the audit log.
        </p>
      )}
    </section>
  );
}
